/**
 * Prochaine etape d'un lead (conception 7.1) : un type, une date et heure, une
 * note. Ce module traduit entre le formulaire (date et heure separees) et
 * l'API (`ProchaineEtape`, instant ISO), et qualifie l'echeance a l'affichage.
 */
import { CalendarCheck, CircleDot, Mail, Phone, Presentation, StickyNote } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TYPES_PROCHAINE_ETAPE, aHeureParis, ajouterJoursOuvres } from '@leads/shared';
import type { ProchaineEtape, SuiviCommercialDTO, TypeInteraction, TypeProchaineEtape } from '@leads/shared';
import { ecartEnJours, formaterDate, formaterHeure, pluriel, versDate, versValeurInputDate } from './format';

export const ICONES_ECHANGE: Readonly<Record<TypeInteraction, LucideIcon>> = {
  APPEL: Phone, EMAIL: Mail, NOTE: StickyNote, RDV: CalendarCheck, DEMO: Presentation, AUTRE: CircleDot,
};

/** Pastille de l'icone : la teinte double le libelle, elle ne le remplace jamais. */
export const TEINTES_ECHANGE: Readonly<Record<TypeInteraction, string>> = {
  APPEL: 'bg-info-50 text-info-700',
  EMAIL: 'bg-accent-50 text-accent-700',
  NOTE: 'bg-surface-appuyee text-encre-douce',
  RDV: 'bg-avertissement-50 text-avertissement-700',
  DEMO: 'bg-succes-50 text-succes-700',
  AUTRE: 'bg-surface-appuyee text-encre-douce',
};

/** Etat d'un formulaire : date et heure separees, comme un commercial les saisit. */
export interface BrouillonProchaineEtape {
  type: TypeProchaineEtape;
  /** `AAAA-MM-JJ` (input date). */
  date: string;
  /** `HH:MM` (input time). */
  heure: string;
  note: string;
}

export const HEURE_PAR_DEFAUT = '10:00';

/** Aide sous tout choix d'etape en attente qui tombe sur une etape finale (saisie d'echange, fiche, pipeline). */
export const AIDE_ETAPE_FINALE = 'Une étape finale efface la prochaine étape.';

export function estTypeProchaineEtape(valeur: string): valeur is TypeProchaineEtape {
  return (TYPES_PROCHAINE_ETAPE as readonly string[]).includes(valeur);
}

/**
 * Proposition de depart : un appel dans `joursOuvres` jours ouvres, a 10 h a
 * Paris. Meme calendrier que les suggestions apres un appel et que l'API : le
 * jour se compte a Paris, quel que soit le fuseau du navigateur.
 */
export function prochaineEtapeParDefaut(maintenant: Date, joursOuvres = 2): BrouillonProchaineEtape {
  return brouillonDepuisInstant('APPEL', aHeureParis(ajouterJoursOuvres(maintenant, joursOuvres), 10).toISOString());
}

/** Brouillon d'une echeance donnee en instant (suggestion partagee) : date et heure du navigateur. */
export function brouillonDepuisInstant(type: TypeProchaineEtape, instant: string, note = ''): BrouillonProchaineEtape {
  return { type, date: versValeurInputDate(instant), heure: formaterHeure(instant), note };
}

/** Prochaine etape planifiee sur le lead ; `null` s'il n'y en a pas. Sans type, c'est un appel. */
export function brouillonDepuisSuivi(suivi: SuiviCommercialDTO | null | undefined): BrouillonProchaineEtape | null {
  const date = versDate(suivi?.dateProchaineAction);
  if (!suivi || !date) return null;
  return { type: suivi.typeProchaineAction ?? 'APPEL', date: versValeurInputDate(date), heure: formaterHeure(date), note: suivi.prochaineAction ?? '' };
}

/** Titre d'une prochaine etape sans date, a la place de son echeance. */
export const LIBELLE_SANS_DATE = 'À faire (sans date)';

/**
 * Texte d'une prochaine etape sans date (donnees anterieures a la refonte) ;
 * `null` si elle est datee ou absente. Elle s'affiche « A faire (sans date) »,
 * et son texte devient la note de la planification suivante : sans cela, il
 * serait efface en silence.
 */
export function texteSansDate(suivi: Pick<SuiviCommercialDTO, 'prochaineAction' | 'dateProchaineAction'> | null | undefined): string | null {
  if (!suivi || versDate(suivi.dateProchaineAction)) return null;
  return suivi.prochaineAction?.trim() || null;
}

/** Point de depart d'une planification : l'etape planifiee, sinon la proposition par defaut, texte sans date repris en note. */
export function brouillonDeDepart(suivi: SuiviCommercialDTO | null | undefined, maintenant: Date, joursOuvres = 2): BrouillonProchaineEtape {
  return brouillonDepuisSuivi(suivi) ?? { ...prochaineEtapeParDefaut(maintenant, joursOuvres), note: texteSansDate(suivi) ?? '' };
}

/** Corps de l'API ; `null` tant que la date manque (le formulaire le signale avant l'envoi). */
export function versProchaineEtape(brouillon: BrouillonProchaineEtape): ProchaineEtape | null {
  if (!brouillon.date) return null;
  // Sans decalage horaire, `AAAA-MM-JJTHH:MM:SS` est lu en heure locale : celle du commercial.
  const date = new Date(`${brouillon.date}T${brouillon.heure || HEURE_PAR_DEFAUT}:00`);
  if (Number.isNaN(date.getTime())) return null;
  return { type: brouillon.type, date: date.toISOString(), note: brouillon.note.trim() || null };
}

export type EtatEcheance = 'en-retard' | 'aujourdhui' | 'a-venir';

/** Le retard se juge au jour : une etape prevue ce matin reste « du jour », comme dans Relances. */
export function etatEcheance(valeur: string | Date, maintenant: Date = new Date()): EtatEcheance {
  const date = versDate(valeur);
  if (!date) return 'a-venir';
  const ecart = ecartEnJours(date, maintenant);
  if (ecart < 0) return 'en-retard';
  return ecart === 0 ? 'aujourdhui' : 'a-venir';
}

/** « En retard de 3 jours », « Aujourd'hui à 10:00 », « Demain à 10:00 », « 02/10/2026 à 10:00 ». */
export function libelleEcheance(valeur: string | Date, maintenant: Date = new Date()): string {
  const date = versDate(valeur);
  if (!date) return '—';
  const ecart = ecartEnJours(date, maintenant);
  const heure = formaterHeure(date);
  if (ecart < 0) return `En retard de ${pluriel(-ecart, 'jour')}`;
  if (ecart === 0) return `Aujourd'hui à ${heure}`;
  if (ecart === 1) return `Demain à ${heure}`;
  return `${formaterDate(date)} à ${heure}`;
}

export const CLASSES_ECHEANCE: Readonly<Record<EtatEcheance, string>> = {
  'en-retard': 'text-danger-700',
  aujourdhui: 'text-avertissement-700',
  'a-venir': 'text-encre-douce',
};
